import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {Divider, makeStyles} from '@material-ui/core'
import useLangEn from 'hooks/useLangEn'
import HealthCenterReportStyle from 'pages/HealthCenter/ReportV1/Style'
import WrapElement from 'pages/HealthCenter/ReportV1/Elements/WrapElement'
import OverActive from 'pages/HealthCenter/ReportV1/Elements/OverActive.svg'
import BalancedIcon from 'pages/HealthCenter/ReportV1/Elements/BalancedIcon.svg'
import UnderactiveIcon from 'pages/HealthCenter/ReportV1/Elements/UnderactiveIcon.svg'
import {AgingLobeScore} from 'api/healthCareApi'
import useLanguage from 'features/languageReport/useLanguage'

const useStyles = makeStyles(HealthCenterReportStyle)

type StatusAPIType = {
  low: string
  normal: string
  high: string
} | null

const scoreToString = (score: number | string, statusAPI: StatusAPIType) => {
  const parsedScore =
    typeof score === 'string' ? Number.parseFloat(score) : score
  if (Math.floor(parsedScore) < 30) {
    return statusAPI?.low ?? ''
  }
  if (Math.floor(parsedScore) > 70) {
    return statusAPI?.high ?? ''
  }

  return statusAPI?.normal ?? ''
}

const scoreSign = (score: number | string) => {
  const parsedScore =
    typeof score === 'string' ? Number.parseFloat(score) : score

  if (Math.floor(parsedScore) < 30) {
    return UnderactiveIcon
  }
  if (Math.floor(parsedScore) > 70) {
    return OverActive
  }

  return BalancedIcon
}

const scoreToClass = (score: number | string) => {
  const parsedScore =
    typeof score === 'string' ? Number.parseFloat(score) : score

  if (Math.floor(parsedScore) < 30) {
    return 'Underactive'
  }
  if (Math.floor(parsedScore) > 70) {
    return 'OverActive'
  }

  return 'Balanced'
}

const Label = ({
  score,
  statusAPI,
}: {
  score: number | string
  statusAPI: StatusAPIType
}) => {
  const {t} = useTranslation()
  const classes = useStyles()
  const string = scoreToString(score, statusAPI)
  const className = scoreToClass(Number(score))
  return (
    <div className={`${classes.Label} ${className}`}>
      {t(string)}
      <img src={scoreSign(score)} alt='sign-icon' />
    </div>
  )
}

const Score = ({
  score,
  statusAPI,
}: {
  score: number | string
  statusAPI: StatusAPIType
}) => {
  const {t, i18n} = useTranslation()
  const classes = useStyles()
  return (
    <div className={classes.scoreContainer}>
      <div className='Score'>
        {score}
        {i18n.language !== 'en' && t('IScore')}
      </div>
      <Label score={score} statusAPI={statusAPI} />
    </div>
  )
}

const RelatedSymptoms = ({arr}: {arr: string[]}) => {
  const classes = useStyles()

  return (
    <div className={classes.symptoms}>
      <p>{arr[0]}</p>
      <p>
        <span>{arr[1]} : </span>
        {arr[2]}
      </p>
      <p>
        <span>{arr[3]} : </span>
        {arr[4]}
      </p>
    </div>
  )
}

const TableFiguresByPart = ({
  agingLobeScore,
  statusAPI,
}: {
  agingLobeScore: AgingLobeScore

  statusAPI: StatusAPIType
}) => {
  const {textHealthAPI} = useLanguage('health')
  const classes = useStyles()
  const textAPI = useMemo(() => {
    if (textHealthAPI?.brain_mapping.basic)
      return textHealthAPI?.brain_mapping.basic
    return null
  }, [textHealthAPI])

  const getNumber = (value: number | string) => {
    if (!Number.isNaN(Number(value))) return Math.floor(Number(value))
    return 0
  }

  const DATA = useMemo(
    () => ({
      head: textAPI?.section.lobes.score_table.headers ?? [],
      body: [
        [
          textAPI?.section.lobes.score_table.rows[0].lobe ?? '',
          [
            textAPI?.section.lobes.score_table.rows[0].functions.total ?? '',
            ...(textAPI?.section.lobes.score_table.rows[0].functions.left ?? [
              '',
              '',
            ]),
            ...(textAPI?.section.lobes.score_table.rows[0].functions.right ?? [
              '',
              '',
            ]),
          ],
          getNumber(agingLobeScore?.Frontal_LH),
          getNumber(agingLobeScore?.Frontal_RH),
        ],
        [
          textAPI?.section.lobes.score_table.rows[1].lobe ?? '',
          [
            textAPI?.section.lobes.score_table.rows[1].functions.total ?? '',
            ...(textAPI?.section.lobes.score_table.rows[1].functions.left ?? [
              '',
              '',
            ]),
            ...(textAPI?.section.lobes.score_table.rows[1].functions.right ?? [
              '',
              '',
            ]),
          ],
          getNumber(agingLobeScore?.Temporal_LH),
          getNumber(agingLobeScore?.Temporal_RH),
        ],
        [
          textAPI?.section.lobes.score_table.rows[2].lobe ?? '',
          [
            textAPI?.section.lobes.score_table.rows[2].functions.total ?? '',
            ...(textAPI?.section.lobes.score_table.rows[2].functions.left ?? [
              '',
              '',
            ]),
            ...(textAPI?.section.lobes.score_table.rows[2].functions.right ?? [
              '',
              '',
            ]),
          ],
          getNumber(agingLobeScore?.Parietal_LH),
          getNumber(agingLobeScore?.Parietal_RH),
        ],
        [
          textAPI?.section.lobes.score_table.rows[3].lobe ?? '',
          [
            textAPI?.section.lobes.score_table.rows[3].functions.total ?? '',
            ...(textAPI?.section.lobes.score_table.rows[3].functions.left ?? [
              '',
              '',
            ]),
            ...(textAPI?.section.lobes.score_table.rows[3].functions.right ?? [
              '',
              '',
            ]),
          ],
          getNumber(agingLobeScore?.Occipital_LH),
          getNumber(agingLobeScore?.Occipital_RH),
        ],
      ],
    }),
    [textHealthAPI],
  )
  return (
    <div className={classes.tableFiguresByPart}>
      <table>
        <thead>
          <tr>
            {DATA.head.map((item) => (
              <th key={item}>{item}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {DATA.body.map((item, rowIndex) => {
            return (
              <tr key={rowIndex}>
                {item.map((cell, cellIndex) => {
                  if (Array.isArray(cell)) {
                    return (
                      <td className={classes.secondCol}>
                        <RelatedSymptoms key={cellIndex} arr={cell} />
                      </td>
                    )
                  }
                  if (Number(cell)) {
                    return (
                      <td className={classes.scoreCol}>
                        <Score score={cell} statusAPI={statusAPI} />
                      </td>
                    )
                  }
                  return (
                    <td className={classes.firstCol} key={cellIndex}>
                      <p>{cell}</p>
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export interface BrainFunctionBasicProps {
  agingThreeDimensionImage: string
  agingContourImage: string
  agingLobeScore: AgingLobeScore
  isPrint?: boolean
  footer: string
}

const BrainFunctionBasic = ({
  agingThreeDimensionImage,
  agingContourImage,
  agingLobeScore,
  isPrint,
  footer,
}: BrainFunctionBasicProps) => {
  const {textHealthAPI} = useLanguage('health')
  const classes = useStyles()
  const isEn = useLangEn()
  const textAPI = useMemo(() => {
    if (textHealthAPI?.brain_mapping.basic)
      return textHealthAPI?.brain_mapping.basic
    return null
  }, [textHealthAPI])

  const statusAPI = useMemo(() => {
    if (textHealthAPI?.brain_mapping.status)
      return textHealthAPI?.brain_mapping.status
    return null
  }, [textHealthAPI])

  return (
    <div className={classes.brainBasic}>
      <WrapElement number={1} text={textAPI?.section.graphs.title ?? ''}>
        <div className={classes.groupImage}>
          <img
            className={classes.image}
            alt='pic1'
            src={agingThreeDimensionImage}
          />
          <img className={classes.image} alt='pic2' src={agingContourImage} />
        </div>
        <div className={classes.groupText}>
          {textAPI?.section.graphs.annotation.map((item) => (
            <p className={classes.textDescription}>{item}</p>
          ))}
        </div>
      </WrapElement>

      <WrapElement number={2} text={textAPI?.section.lobes.title ?? ''}>
        <TableFiguresByPart
          agingLobeScore={agingLobeScore}
          statusAPI={statusAPI}
        />
        <div className={classes.groupText}>
          <p className={classes.textDescription}>
            <span>*</span>
            {textAPI?.section.lobes.score_table.annotation ?? ''}
          </p>
        </div>
        <div
          className={`${classes.brainBasicDescription} ${
            isPrint && isEn ? classes.mtPrint : ''
          }`}
        >
          <div>
            <Label score={9} statusAPI={statusAPI} />
            <p>{textAPI?.section.lobes.description.low[0] ?? ''}</p>
            <ul>
              <li>{textAPI?.section.lobes.description.low[1] ?? ''}</li>
              <li>{textAPI?.section.lobes.description.low[2] ?? ''}</li>
            </ul>
          </div>
          <div>
            <Label score={99} statusAPI={statusAPI} />
            <p>{textAPI?.section.lobes.description.high[0] ?? ''}</p>
            <ul>
              <li>{textAPI?.section.lobes.description.high[1] ?? ''}</li>
              <li>{textAPI?.section.lobes.description.high[2] ?? ''}</li>
            </ul>
          </div>
        </div>
      </WrapElement>
      <Divider style={{marginLeft: 10, marginRight: 10, marginBottom: 10}} />
      <div className={classes.footer}>{footer}</div>
    </div>
  )
}

export default BrainFunctionBasic
